import { useSyncExternalStore } from 'react';

/**
 * The theme the shell is actually rendering in, resolved in code.
 *
 * CLM-65 — the first production sign-in showed BOTH brand logos on the dark
 * theme. The cause was a two-element CSS swap (`.lg-lt` / `.lg-dk`) that had to
 * agree with itself across the stamped `data-theme` attribute and the
 * `prefers-color-scheme` media query; when the two disagreed, two rules fired
 * and two images showed. Resolving the theme once, here, and rendering ONE
 * element removes the class of bug rather than patching a CSS state (D-65-1).
 *
 * Resolution order matches tokens.css: an explicit `data-theme="dark"` /
 * `"light"` on the root element wins; otherwise the viewer's system preference
 * decides. Both sources are subscribed to — a MutationObserver on the attribute
 * and a `change` listener on the media query — and both are released on unmount.
 *
 * jsdom has no `matchMedia`. When the function is absent the hook reports
 * 'light', which is also what a browser with no preference expressed would get.
 */
export type ResolvedTheme = 'dark' | 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function mediaQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DARK_QUERY);
}

/** Read the current theme once, with no subscription. Exported for tests. */
export function readResolvedTheme(): ResolvedTheme {
  if (typeof document === 'undefined') return 'light';
  const stamped = document.documentElement.dataset['theme'];
  if (stamped === 'dark' || stamped === 'light') return stamped;
  return mediaQuery()?.matches === true ? 'dark' : 'light';
}

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  const query = mediaQuery();
  query?.addEventListener('change', onChange);

  return () => {
    observer.disconnect();
    query?.removeEventListener('change', onChange);
  };
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, readResolvedTheme, () => 'light');
}
